<template>
  <div class="limit-recalculation mb-3">
    <div class="d-flex flex-wrap align-items-center">
      <b-form-radio-group v-model="scope" class="mr-3 mb-2" buttons button-variant="outline-secondary"
        :disabled="recalcRunning || recalcChecking || recalcUncertain" aria-label="Recalculation scope">
        <b-form-radio value="selected">Selected limits ({{ selectedIds.length }})</b-form-radio>
        <b-form-radio value="all">All limits</b-form-radio>
      </b-form-radio-group>
      <b-button v-if="selectedIds.length" variant="outline-secondary" class="mr-3 mb-2"
        title="Clear selection" aria-label="Clear selection"
        :disabled="recalcRunning || recalcUncertain" @click="$emit('restore-selection', [])">
        <i class="fas fa-times" aria-hidden="true"></i>
      </b-button>
      <b-form-checkbox v-model="recalcDryRun" class="mr-3 mb-2"
        :disabled="recalcRunning || recalcChecking || recalcUncertain">Dry run (no publication)</b-form-checkbox>
      <b-button type="button" class="mb-2" :variant="recalcDryRun ? 'primary' : 'warning'"
        :disabled="!recalcEnabled || recalcRunning || recalcChecking || (!recalcDryRun && !recalcPublicationEnabled)"
        @click="runRecalculation">
        <i class="fas fa-sync" :class="{ 'fa-spin': recalcRunning }" aria-hidden="true"></i>
        {{ recalcSubmitting ? 'Queuing...' : recalcUncertain ? 'Retry queue request' : recalcDryRun ? 'Check chips & limits (dry run)' : 'Recalculate & publish' }}
      </b-button>
    </div>
    <section class="recalculation-status mb-3 px-3" aria-label="Recalculation status" aria-live="polite" :aria-busy="recalcRunning">
          <b-button type="button" variant="outline-secondary" class="mb-2" :disabled="recalcChecking || recalcSubmitting" @click="refreshRecalculation(true)" title="Refresh recalculation status" aria-label="Refresh recalculation status"><i class="fas fa-sync" :class="{ 'fa-spin': recalcChecking }" aria-hidden="true"></i></b-button>
          <p v-if="recalcChecking && !recalcJob" class="text-muted">Loading recalculation status...</p>
          <p v-else-if="recalcAvailabilityKnown && !recalcEnabled" class="text-muted">Recalculation is disabled</p>
          <b-alert v-if="recalcAvailabilityKnown && recalcEnabled && !recalcPublicationEnabled" show variant="warning">Publication is disabled until game clients support refreshed limits and chips. Dry run is available; live limits will not change.</b-alert>
          <p v-if="activeReleaseId" class="mb-2">Active release: <strong>{{ activeReleaseId }}</strong></p>
          <template v-if="recalcJob">
            <h6>{{ recalcRunning ? 'Current job' : 'Last job result' }}</h6>
            <p class="mb-2"><b-badge :variant="recalcStatusVariant">{{ recalcJob.status }}</b-badge> <span class="ml-2">Job: {{ recalcJob.jobId }}</span></p>
            <p class="mb-2">Scope: {{ jobScope }}</p>
            <p class="mb-2">Mode: {{ recalcJobDryRun ? 'Dry run (no publication)' : 'Publication' }}</p>
            <p v-if="recalcRunning" class="mb-2" role="status"><i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>{{ recalcJob.status === 'queued' ? 'Waiting for worker' : 'Recalculation in progress' }}</p>
            <p v-if="recalcJob.message" :class="{ 'text-danger': recalcJob.status === 'failed' }">{{ recalcJob.message }}</p>
            <b-alert v-if="recalcJob.warning" show variant="warning">{{ recalcJob.warning }}</b-alert>
            <b-alert v-for="(warning, index) in recalcJob.warnings || []" :key="`recalc-warning-${index}`" show variant="warning">{{ warning }}</b-alert>
            <p v-if="recalcJob.releaseId" class="mb-2">{{ recalcJobDryRun ? 'Candidate release' : 'Release' }}: {{ recalcJob.releaseId }}</p>
            <p v-if="recalcJob.updated !== undefined || recalcJob.skipped !== undefined" class="mb-2"><span v-if="recalcJob.updated !== undefined" class="mr-3">{{ recalcJobDryRun ? 'Calculated' : 'Updated' }}: {{ recalcJob.updated }}</span><span v-if="recalcJob.skipped !== undefined">Skipped: {{ recalcJob.skippedCount !== undefined ? recalcJob.skippedCount : Array.isArray(recalcJob.skipped) ? recalcJob.skipped.length : recalcJob.skipped }}</span></p>
            <ul v-if="Array.isArray(recalcJob.skipped) && recalcJob.skipped.length" class="pl-3"><li v-for="(item, index) in recalcJob.skipped" :key="index">{{ typeof item === 'string' ? item : JSON.stringify(item) }}</li></ul>
            <section v-if="recalcJobDryRun && recalcJob.status === 'completed' && recalcJob.preview" class="mt-3" aria-label="Calculated limits preview">
              <h6>Calculated limits ({{ previewItems.length }} / {{ recalcJob.preview.total }})</h6>
              <b-alert v-if="recalcJob.preview.truncated" show variant="warning">Preview size limit reached. Select fewer limits for the remaining results.</b-alert>
              <template v-if="previewItems.length">
                <b-form-group label="Limit" label-for="recalculation-preview-limit">
                  <b-form-select id="recalculation-preview-limit" v-model="previewLimitId" :options="previewOptions" />
                </b-form-group>
                <template v-if="previewEntry">
                  <p class="mb-2">Limit ID: {{ previewEntry.limitId }}</p>
                  <p v-if="previewEntry.reason" class="text-warning">{{ previewEntry.reason }}</p>
                  <p v-else>{{ previewEntry.calculated ? 'Generated from EUR' : 'Own settings' }}</p>
                  <p v-if="previewEntry.toEUR" class="mb-2">In EUR: {{ previewEntry.toEUR }}</p>
                  <b-form-radio-group v-model="previewProfile" :options="[{ text: 'Normal', value: 'normal' }, { text: 'Peak', value: 'peak' }]" buttons button-variant="outline-primary" size="sm" class="mb-3" aria-label="Calculated profile" />
                  <LimitSettingsPreview :settings="previewSettings" :empty-text="previewProfile === 'peak' ? 'No calculated peak settings' : 'No calculated normal settings'" />
                  <template v-if="previewProfile === 'peak' && previewEntry.schedule">
                    <h6>Peak schedule: {{ previewEntry.schedule.timezone }}</h6>
                    <ul class="pl-3"><li v-for="(window, index) in previewEntry.schedule.windows || []" :key="index">{{ window.days.map(day => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][day - 1]).join(', ') }}: {{ window.start }} - {{ window.end }}</li></ul>
                  </template>
                </template>
              </template>
            </section>
          </template>
          <b-alert v-if="recalcError" show variant="danger">{{ recalcError }}</b-alert>
        </section>
  </div>
</template>

<script>
import LimitSettingsPreview from './LimitSettingsPreview.vue';

export default {
  components: { LimitSettingsPreview },
  props: { selectedIds: { type: Array, default: () => [] } },
  data() {
    return {
      scope: 'selected',
      recalcEnabled: false,
      recalcPublicationEnabled: false,
      recalcDryRun: true,
      recalcRequestOptions: null,
      recalcAvailabilityKnown: false,
      recalcSubmitting: false,
      recalcChecking: false,
      recalcUncertain: false,
      recalcRequestId: null,
      recalcJob: null,
      recalcCompletedJobId: null,
      recalcActiveRelease: null,
      recalcError: '',
      recalcTimer: null,
      recalcDisposed: false,
      previewLimitId: null,
      previewProfile: 'normal',
    };
  },
  computed: {
    previewItems() { return this.recalcJob?.preview?.items || []; },
    previewOptions() {
      return this.previewItems.map(item => ({ value: item.limitId, text: 'Table ' + item.tableId + ' / ' + item.currency + ' / Type ' + item.limitType + ' / ' + item.limitId.slice(-6) }));
    },
    previewEntry() { return this.previewItems.find(item => item.limitId === this.previewLimitId) || this.previewItems[0] || null; },
    previewSettings() { return this.previewEntry?.[this.previewProfile]?.settings || null; },
    jobScope() {
      const options = this.recalcJob?.options || this.recalcRequestOptions;
      return options?.limitIds ? 'Selected limits (' + options.limitIds.length + ')' : 'All limits';
    },
    recalcRunning() {
      return this.recalcSubmitting || !!(this.recalcJob && ['queued', 'running'].includes(this.recalcJob.status));
    },
    recalcStatusVariant() {
      return { queued: 'secondary', running: 'info', completed: 'success', failed: 'danger' }[this.recalcJob?.status] || 'secondary';
    },
    recalcJobDryRun() {
      return this.recalcJob?.options?.dryRun ?? this.recalcJob?.dryRun ?? this.recalcRequestOptions?.dryRun ?? false;
    },
    activeReleaseId() {
      return typeof this.recalcActiveRelease === 'object' ? this.recalcActiveRelease?.releaseId || null : this.recalcActiveRelease;
    },
  },
  watch: {
    selectedIds(value) {
      if (value.length && !this.recalcRunning && !this.recalcUncertain) this.scope = 'selected';
    },
    recalcRunning(value) { this.$emit('busy', value || this.recalcUncertain); },
    recalcUncertain(value) { this.$emit('busy', value || this.recalcRunning); },
  },
  methods: {
    scheduleRecalculationPoll() {
      clearTimeout(this.recalcTimer);
      if (!this.recalcDisposed && this.recalcRunning) this.recalcTimer = setTimeout(() => this.refreshRecalculation(), 2500);
    },
    persistRecalculation() {
      try {
        sessionStorage.setItem('alg577.recalculation', JSON.stringify({ requestId: this.recalcRequestId, options: this.recalcRequestOptions, jobId: this.recalcJob?.jobId || null, uncertain: this.recalcUncertain }));
      } catch { /* Status still works when browser storage is unavailable. */ }
    },
    restoreRecalculation() {
      try {
        const state = JSON.parse(sessionStorage.getItem('alg577.recalculation'));
        if (!state) return;
        const ids = state.options?.limitIds;
        if (ids !== undefined && ids !== null && (!Array.isArray(ids) || !ids.length || ids.length > 1000 || ids.some(id => !/^[a-f0-9]{24}$/i.test(id)))) return;
        this.recalcRequestId = /^[A-Za-z0-9_-]{16,80}$/.test(state.requestId) ? state.requestId : null;
        this.recalcUncertain = !!state.uncertain && !!this.recalcRequestId;
        // Requests made before the dry-run selector were always publications.
        this.recalcRequestOptions = { currency: null, dryRun: state.options?.dryRun === true, skipFetch: false };
        if (ids) {
          this.recalcRequestOptions.limitIds = [...new Set(ids.map(id => id.toLowerCase()))].sort();
          this.scope = 'selected';
          this.$emit('restore-selection', this.recalcRequestOptions.limitIds);
        } else {
          this.scope = 'all';
        }
        this.recalcDryRun = this.recalcRequestOptions.dryRun;
        if (/^[A-Za-z0-9_-]{16,80}$/.test(state.jobId) && !this.recalcUncertain) this.recalcJob = { jobId: state.jobId, status: 'queued' };
      } catch { /* Ignore invalid saved status. */ }
    },
    applyRecalculationJob(job, acknowledgement = false) {
      if (!job || !job.jobId || !['queued', 'running', 'completed', 'failed'].includes(job.status)) throw new Error('Unexpected recalculation response. Refresh status before retrying.');
      if (this.recalcJob?.jobId !== job.jobId || this.recalcJob?.status !== 'completed') {
        this.previewLimitId = job.preview?.items?.[0]?.limitId || null;
        this.previewProfile = 'normal';
      }
      this.recalcJob = job;
      this.persistRecalculation();
      if (!acknowledgement && job.status === 'completed' && this.recalcCompletedJobId !== job.jobId) {
        this.recalcCompletedJobId = job.jobId;
        if (job.releaseId && !this.recalcJobDryRun) this.recalcActiveRelease = job.releaseId;
        this.$emit('completed');
      }
    },
    async refreshRecalculation(checkAvailability = false) {
      if (this.recalcChecking || this.recalcSubmitting || this.recalcDisposed) return;
      clearTimeout(this.recalcTimer);
      this.recalcChecking = true;
      this.recalcError = '';
      const jobId = this.recalcJob?.jobId || (this.recalcUncertain ? this.recalcRequestId : null);
      try {
        if (checkAvailability || !this.recalcAvailabilityKnown || !jobId) {
          const { data } = await this.$http({ url: '/api/v1/currencies/recalculate-auto', method: 'GET' });
          if (this.recalcDisposed) return;
          if (typeof data.enabled !== 'boolean') throw new Error('Could not determine recalculation availability.');
          this.recalcEnabled = data.enabled;
          this.recalcPublicationEnabled = data.publicationEnabled === true;
          this.recalcAvailabilityKnown = true;
          this.recalcActiveRelease = data.activeRelease || null;
          if (data.job && !jobId) this.applyRecalculationJob(data.job);
        }
        if (jobId) {
          const { data } = await this.$http({ url: '/api/v1/currencies/recalculate-auto', method: 'GET', params: { jobId } });
          if (this.recalcDisposed) return;
          this.applyRecalculationJob({ ...data, jobId });
        }
        if (jobId) this.recalcUncertain = false;
        this.persistRecalculation();
        this.scheduleRecalculationPoll();
      } catch (err) {
        if (!this.recalcDisposed) this.recalcError = err.response?.data?.message || err.message || 'Could not refresh recalculation status.';
        if (err.response?.status === 404 && !this.recalcJob && jobId) {
          this.recalcUncertain = false;
          this.persistRecalculation();
        }
      } finally {
        this.recalcChecking = false;
        this.scheduleRecalculationPoll();
      }
    },
    async runRecalculation() {
      if (!this.recalcEnabled || this.recalcRunning || this.recalcChecking) return;
      if (!this.recalcDryRun && !this.recalcPublicationEnabled) {
        this.recalcError = 'Publication is disabled. Select Dry run to check limits without changing live values.';
        return;
      }
      if (!this.recalcUncertain && this.scope === 'selected' && !this.selectedIds.length) {
        this.recalcError = 'Select at least one limit.';
        return;
      }
      if (!this.recalcUncertain && this.selectedIds.length > 1000 && this.scope === 'selected') {
        this.recalcError = 'Select at most 1000 limits.';
        return;
      }
      const options = { currency: null, dryRun: this.recalcDryRun, skipFetch: false };
      if (this.scope === 'selected') options.limitIds = [...new Set(this.selectedIds)].sort();
      if (!this.recalcRequestId || (!this.recalcUncertain && (JSON.stringify(options) !== JSON.stringify(this.recalcRequestOptions) || this.recalcJob && ['completed', 'failed'].includes(this.recalcJob.status)))) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        this.recalcRequestId = 'limits-' + Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
      }
      this.recalcRequestOptions = this.recalcUncertain ? this.recalcRequestOptions : options;
      this.recalcSubmitting = true;
      this.recalcError = '';
      this.recalcUncertain = true;
      this.recalcJob = null;
      this.persistRecalculation();
      try {
        const { data } = await this.$http({ url: '/api/v1/currencies/recalculate-auto', method: 'POST', data: { requestId: this.recalcRequestId, ...this.recalcRequestOptions } });
        if (!this.recalcDisposed) {
          this.applyRecalculationJob(data, true);
          this.recalcUncertain = false;
          this.persistRecalculation();
        }
      } catch (err) {
        if (!this.recalcDisposed) {
          this.recalcError = err.response?.data?.message || err.message || 'Could not queue recalculation. Refresh status before retrying.';
          // A lost POST response does not prove that the worker job was not queued.
          this.recalcUncertain = !err.response || err.response.status >= 500;
          if (err.response?.status === 409) this.recalcPublicationEnabled = false;
          this.persistRecalculation();
        }
      } finally {
        this.recalcSubmitting = false;
        // POST only returns an acknowledgement, including on idempotent replay.
        if (!this.recalcUncertain) await this.refreshRecalculation();
      }
    },
  },
  mounted() {
    this.restoreRecalculation();
    this.refreshRecalculation();
  },
  beforeDestroy() {
    this.recalcDisposed = true;
    clearTimeout(this.recalcTimer);
  },
};
</script>

<style scoped>
.recalculation-status { overflow-wrap: anywhere; }
</style>
